import { buildGenerationReferenceIdentity } from '@/lib/generation/generation-reference-draft-reconcile';
import type {
  GenerationReferenceAddOperation,
  GenerationReferenceDraftOperation,
  GenerationReferenceItem,
  GenerationReferenceRemoveOperation,
} from '@/types/generation-reference';

/** 资源身份（不含 role）：用于识别「同一资源换 role」。 */
function operationResourceKey(
  operation: GenerationReferenceAddOperation | GenerationReferenceRemoveOperation
): string {
  return operation.operation === 'add'
    ? `${operation.reference.resourceType}:${operation.reference.resourceId}`
    : `${operation.resourceType}:${operation.resourceId}`;
}

/**
 * 操作排序：把「同一资源换 role」（remap）的 remove 排到全部 add 之后。
 *
 * 草稿边的 identity 含 role，改 role 必须表达为 remove + add 两条操作，而
 * `applyGenerationReferenceDraftOperations` 是逐条串行 HTTP、服务端每条都广播给发起方自己。
 * 若 remove 先落地，画布边会真实消失一个网络往返（该节点只剩这一条引用时，快照里整个 draft
 * 都会被摘掉），且 add 失败即永久断线——这正是「切模式不该断连线」要消除的现象。
 *
 * add 先落地是安全的：唯一索引 `uq_generation_reference_draft_edges_identity` 含 role，
 * 同资源两个 role 可共存；add 接口也没有条数上限校验。中间态两条边的 source/target 相同、
 * 路径完全重合，肉眼无差异。
 *
 * 非 remap 场景（纯删、纯增、删增不同资源）顺序保持原样：先 remove 再 add。
 */
function sortOperationsForRoleRemap(
  removes: GenerationReferenceRemoveOperation[],
  adds: GenerationReferenceAddOperation[]
): GenerationReferenceDraftOperation[] {
  if (removes.length === 0 || adds.length === 0) return [...removes, ...adds];
  const addedResourceKeys = new Set(adds.map(operationResourceKey));
  const remapRemoves: GenerationReferenceRemoveOperation[] = [];
  const plainRemoves: GenerationReferenceRemoveOperation[] = [];
  for (const remove of removes) {
    if (addedResourceKeys.has(operationResourceKey(remove))) remapRemoves.push(remove);
    else plainRemoves.push(remove);
  }
  return [...plainRemoves, ...adds, ...remapRemoves];
}

/**
 * 计算从 current 引用集合迁移到 desired 集合所需的最小 add/remove 操作。
 * 以引用身份 (resourceType, resourceId, role) 去重，保证幂等。
 */
export function buildGenerationReferenceDraftOperations(
  current: GenerationReferenceItem[],
  desired: GenerationReferenceItem[]
): GenerationReferenceDraftOperation[] {
  const currentByIdentity = new Map(
    current.map((reference) => [buildGenerationReferenceIdentity(reference), reference])
  );
  const desiredByIdentity = new Map(
    desired.map((reference) => [buildGenerationReferenceIdentity(reference), reference])
  );
  const removes: GenerationReferenceRemoveOperation[] = [];
  for (const [identity, reference] of currentByIdentity) {
    if (!desiredByIdentity.has(identity)) {
      removes.push({
        operation: 'remove',
        resourceType: reference.resourceType,
        resourceId: reference.resourceId,
        role: reference.role,
      });
    }
  }
  const adds: GenerationReferenceAddOperation[] = [];
  for (const [identity, reference] of desiredByIdentity) {
    if (!currentByIdentity.has(identity)) {
      adds.push({ operation: 'add', reference });
    }
  }
  return sortOperationsForRoleRemap(removes, adds);
}

/**
 * CAS 冲突重放 / 协作并发：在服务端最新集合上，只落地「相对本端旧快照」的意图。
 * - add：desired 有且 server 无
 * - remove：本端旧快照有、desired 无，且 server 仍有
 * - forceRemoveIdentities：面板/剪刀等显式断开（画布拉线写入的引用可能不在 lastComposerDesired）
 * 不删除「本端未见过、别人刚加上」的引用（避免双 Tab 把对方新增 remove 掉）。
 */
export function buildRebasedGenerationReferenceDraftOperations(params: {
  serverCurrent: GenerationReferenceItem[];
  baseCurrent: GenerationReferenceItem[];
  desired: GenerationReferenceItem[];
  forceRemoveIdentities?: ReadonlyArray<string>;
}): GenerationReferenceDraftOperation[] {
  const serverByIdentity = new Map(
    params.serverCurrent.map((reference) => [
      buildGenerationReferenceIdentity(reference),
      reference,
    ])
  );
  const baseByIdentity = new Map(
    params.baseCurrent.map((reference) => [buildGenerationReferenceIdentity(reference), reference])
  );
  const desiredByIdentity = new Map(
    params.desired.map((reference) => [buildGenerationReferenceIdentity(reference), reference])
  );
  const forceRemoveIdentities = new Set(params.forceRemoveIdentities ?? []);
  const removes: GenerationReferenceRemoveOperation[] = [];
  for (const [identity, reference] of serverByIdentity) {
    if (desiredByIdentity.has(identity)) continue;
    if (baseByIdentity.has(identity) || forceRemoveIdentities.has(identity)) {
      removes.push({
        operation: 'remove',
        resourceType: reference.resourceType,
        resourceId: reference.resourceId,
        role: reference.role,
      });
    }
  }
  const adds: GenerationReferenceAddOperation[] = [];
  for (const [identity, reference] of desiredByIdentity) {
    if (!serverByIdentity.has(identity)) {
      adds.push({ operation: 'add', reference });
    }
  }
  return sortOperationsForRoleRemap(removes, adds);
}

/**
 * ops 为空时是否推进 lastComposerDesired。
 * 仅当 server ⊆ desired（身份集合）时推进，避免「本想删却被空 base 拦住」时毒化 base。
 * 对端新增仍留在 server、本端 desired 更小且无 forceRemove 时返回 false，保留旧 base。
 */
export function canAdvanceComposerDesiredOnNoop(params: {
  serverCurrent: ReadonlyArray<GenerationReferenceItem>;
  desired: ReadonlyArray<GenerationReferenceItem>;
}): boolean {
  const desiredIdentities = new Set(
    params.desired.map((reference) => buildGenerationReferenceIdentity(reference))
  );
  return params.serverCurrent.every((reference) =>
    desiredIdentities.has(buildGenerationReferenceIdentity(reference))
  );
}
